import { useState } from 'react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Plus, RefreshCw, AlertCircle, Users } from 'lucide-react'
import { UsersTable, UsersFilters, StatsCards, UserFormDialog } from '../components'
import {
  useUsers,
  useUserStats,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useActivateUser,
} from '@/modules/users/hooks/useUsers'
import type { UsersFilters as UsersFiltersType, UserCreateInput, UserUpdateInput } from '@/modules/users/types/user'

export function UsersPage() {
  useDocumentTitle('Usuarios')
  const [filters, setFilters] = useState<UsersFiltersType>({
    page: 1,
    page_size: 25,
  })

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

  // Queries
  const { data: usersData, isLoading: isLoadingUsers, refetch: refetchUsers } = useUsers(filters)
  const { data: stats, isLoading: isLoadingStats } = useUserStats()

  // Mutations
  const createUser = useCreateUser()
  const updateUser = useUpdateUser()
  const deleteUser = useDeleteUser()
  const activateUser = useActivateUser()

  // Handlers
  const handleFiltersChange = (newFilters: UsersFiltersType) => {
    setFilters(newFilters)
  }

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      page_size: 25,
    })
  }

  const handleCreateUser = async (data: UserCreateInput) => {
    const user = await createUser.mutateAsync(data)
    setIsCreateDialogOpen(false)
    return user
  }

  const handleEditUser = async (data: UserUpdateInput) => {
    if (!selectedUserId) return
    await updateUser.mutateAsync({ userId: selectedUserId, data })
    setIsEditDialogOpen(false)
    setSelectedUserId(null)
  }

  const handleToggleActive = async (userId: number, currentStatus: boolean) => {
    if (currentStatus) {
      // User is active, deactivate it
      await deleteUser.mutateAsync(userId)
    } else {
      // User is inactive, activate it
      await activateUser.mutateAsync(userId)
    }
  }

  const handleDelete = async (userId: number) => {
    if (confirm('¿Está seguro de que desea desactivar este usuario?')) {
      await deleteUser.mutateAsync(userId)
    }
  }

  const handleViewUser = (userId: number) => {
    // TODO: Navigate to user detail page or open detail dialog
    console.log('View user:', userId)
  }

  const handleEditClick = (userId: number) => {
    setSelectedUserId(userId)
    setIsEditDialogOpen(true)
  }

  const selectedUser = selectedUserId
    ? usersData?.results.find((u) => u.id === selectedUserId)
    : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Users className="h-6 w-6 text-teal-500" />
          Usuarios
        </h1>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => refetchUsers()}
            disabled={isLoadingUsers}
          >
            <RefreshCw className={`h-4 w-4 ${isLoadingUsers ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setIsCreateDialogOpen(true)} className="bg-teal-800 hover:bg-teal-900 text-white">
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Usuario
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards stats={stats} isLoading={isLoadingStats} />

      {/* Filters */}
      <UsersFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
      />

      {/* Table */}
      <Card className="border border-gray-300 dark:border-gray-700">
        {isLoadingUsers ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : usersData && usersData.results.length > 0 ? (
          <div className="p-6">
            <UsersTable
              users={usersData.results}
              onView={handleViewUser}
              onEdit={handleEditClick}
              onToggleActive={handleToggleActive}
              onDelete={handleDelete}
            />

            {/* Pagination */}
            {usersData.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Mostrando {(filters.page! - 1) * filters.page_size! + 1} -{' '}
                  {Math.min(filters.page! * filters.page_size!, usersData.count)} de{' '}
                  {usersData.count} usuarios
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))
                    }
                    disabled={filters.page === 1}
                  >
                    Anterior
                  </Button>
                  <div className="text-sm">
                    Página {filters.page} de {usersData.total_pages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))
                    }
                    disabled={filters.page === usersData.total_pages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-semibold">No se encontraron usuarios</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Intenta ajustar los filtros o crear un nuevo usuario
            </p>
          </div>
        )}
      </Card>

      {/* Create User Dialog */}
      <UserFormDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={handleCreateUser}
        isSubmitting={createUser.isPending}
        mode="create"
      />

      {/* Edit User Dialog */}
      <UserFormDialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open)
          if (!open) setSelectedUserId(null)
        }}
        onSubmit={handleEditUser}
        isSubmitting={updateUser.isPending}
        user={selectedUser as any}
        mode="edit"
      />
    </div>
  )
}
